import React, { useId } from 'react'
import type { LoyaltyCouponLimits } from '../../../../../types/loyaltyCoupon'
import { CouponLimitToggle } from './CouponLimitToggle'
import * as s from './limitsStyles'

// Mirrors `breadstack-loyalty-pro.html` lines 1053-1080 — the "Expiry" block in
// the Limitations & Restrictions section. Toggle was previously commented out;
// brought back per request, but with inverted semantics: the toggle now reads
// **"Has expiry"** (checked = has an expiry date the user can edit) instead of
// the design's "Does not expire" framing.
//
// When the toggle is OFF (no expiry), `limits.expiresAt` is set to the
// UNEXPIRE_AT sentinel — a far-future ISO string — so the wire payload always
// carries a concrete `date_expires` rather than `null`. The block also still
// treats legacy `null` values as "no expiry" so historical coupons render
// correctly without an in-place rewrite.

// Far-future sentinel used as the "Does not expire" value on the wire. Year
// 2099 is comfortably beyond any real-world coupon lifetime, well within the
// range every storage engine (JS `Date`, Mongo, Postgres, MySQL) accepts, and
// flat-out cheaper to eyeball in logs than `9999-…`. The mapper passes this
// value straight through to `date_expires`, so the backend stores it as-is.
export const COUPON_UNEXPIRE_AT = '2099-12-31T23:59:59'

const isUnexpireAt = (iso: string | null): boolean => iso === COUPON_UNEXPIRE_AT

interface Props {
  limits: LoyaltyCouponLimits
  onChange: (next: LoyaltyCouponLimits) => void
}

// Split an ISO datetime (`2026-12-31T23:59` or `2026-12-31T23:59:00Z`) into
// the date + time halves the two inputs need. Empty values for `null`.
const splitDatetime = (iso: string | null): { date: string; time: string } => {
  if (!iso) return { date: '', time: '23:59' }
  // Drop any zone suffix the value might carry (`Z` / `±HH:mm`).
  const noZone = iso.replace(/(Z|[+-]\d{2}:?\d{2})$/, '')
  const [date = '', timeFull = ''] = noZone.split('T')
  // Take only HH:mm from the time half (drop seconds if present).
  const time = timeFull.slice(0, 5) || '23:59'
  return { date, time }
}

// Default expiry date for the first time the user flips ON "Has expiry" —
// 2 days from now, matches the modal's seed for new coupons.
const todayPlus2 = (): string => {
  const d = new Date()
  d.setDate(d.getDate() + 2)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export const CouponExpiryBlock: React.FC<Props> = ({ limits, onChange }) => {
  // Treat both legacy `null` and the COUPON_UNEXPIRE_AT sentinel as "no
  // expiry" so old data with `null` doesn't show as having a real expiry date.
  const hasExpiry = limits.expiresAt !== null && !isUnexpireAt(limits.expiresAt)
  const { date, time } = splitDatetime(hasExpiry ? limits.expiresAt : null)
  const idDate = useId()
  const idTime = useId()

  const handleToggleHasExpiry = (nextHasExpiry: boolean) => {
    if (nextHasExpiry) {
      // Re-enabling expiry: seed with today + 2d @ 23:59 so the user has a
      // sensible starting point in the date/time inputs.
      onChange({ ...limits, expiresAt: `${todayPlus2()}T23:59` })
    } else {
      // Disabling expiry: store the COUPON_UNEXPIRE_AT sentinel rather than
      // `null` so the backend always receives a concrete `date_expires` value
      // that effectively means "never expires".
      onChange({ ...limits, expiresAt: COUPON_UNEXPIRE_AT })
    }
  }

  const handleDateChange = (nextDate: string) => {
    if (!nextDate) {
      // Clearing the date input falls back to the no-expiry sentinel — same
      // effect as flipping the toggle OFF.
      onChange({ ...limits, expiresAt: COUPON_UNEXPIRE_AT })
      return
    }
    onChange({ ...limits, expiresAt: `${nextDate}T${time || '23:59'}` })
  }

  const handleTimeChange = (nextTime: string) => {
    const safeTime = nextTime || '23:59'
    if (!date) return
    onChange({ ...limits, expiresAt: `${date}T${safeTime}` })
  }

  return (
    <div style={s.block}>
      <div style={s.blockHead}>
        <div>
          <div style={s.blockTitle}>Expiry</div>
          <div style={s.blockDesc}>
            {hasExpiry
              ? 'Expires on the date below'
              : 'No expiry — coupon stays valid until paused or deleted'}
          </div>
        </div>
        <div style={s.toggleWrap} title="Has expiry">
          <span style={s.toggleLabel}>Has expiry</span>
          <CouponLimitToggle
            checked={hasExpiry}
            onChange={handleToggleHasExpiry}
            ariaLabel="Has expiry"
          />
        </div>
      </div>
      {hasExpiry && (
        <div style={s.blockBody}>
          <div style={s.formRow}>
            <div style={s.formGroup}>
              <label style={s.formLabel} htmlFor={idDate}>
                Expiry date
              </label>
              <input
                id={idDate}
                style={s.formInput}
                type="date"
                value={date}
                onChange={(e) => handleDateChange(e.target.value)}
              />
            </div>
            <div style={s.formGroup}>
              <label style={s.formLabel} htmlFor={idTime}>
                Expiry time (optional)
              </label>
              <input
                id={idTime}
                style={s.formInput}
                type="time"
                value={time}
                onChange={(e) => handleTimeChange(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
